import type { QuestionnaireResponse, QuestionnaireResponseItem } from "@medplum/fhirtypes";

import {
  isYesAnswer,
  readYesNoDisplayFromAnswer,
  yesNoDisplayToLoincCoding,
} from "@/components/ClearableYesNoRadioField";
import {
  FETAL_HEART_RATE_ACCELERATIONS_DETECTED_LINK_ID,
  FETAL_HEART_RATE_ASSESS_BASELINE_LINK_ID,
  FETAL_HEART_RATE_BASELINE_DURATION_OF_LISTENING_LINK_ID,
  FETAL_HEART_RATE_BASELINE_LINK_ID,
  FETAL_HEART_RATE_BEATS_PER_MINUTE_HIGH_LINK_ID,
  FETAL_HEART_RATE_BEATS_PER_MINUTE_LINK_ID,
  FETAL_HEART_RATE_BEATS_PER_MINUTE_LOW_LINK_ID,
  FETAL_HEART_RATE_DATE_LINK_ID,
  FETAL_HEART_RATE_DATE_TIME_LINK_ID,
  FETAL_HEART_RATE_DECELERATIONS_DETECTED_LINK_ID,
  FETAL_HEART_RATE_DURATION_OF_LISTENING_LINK_ID,
  FETAL_HEART_RATE_LISTENING_DEVICE_LINK_ID,
  FETAL_HEART_RATE_NOTES_LINK_ID,
  FETAL_HEART_RATE_RELATION_TO_CONTRACTIONS_LINK_ID,
  FETAL_HEART_RATE_ROOT_LINK_ID,
  FETAL_HEART_RATE_SIGNATURE_INITIALS_LINK_ID,
  FETAL_HEART_RATE_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/constants/fetal-heart-rate-link-ids";
import type { FetalHeartRateFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/types/fetal-heart-rate-form";
import { getEmptyFetalHeartRateFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/utils/fetal-heart-rate-form-defaults";
import {
  parseCodingFromNativeSelect,
  readCodingAnswerAsNativeSelectValue,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import {
  findQuestionnaireResponseItem,
  parseQuestionnaireResponseLocalValueDate,
  readQuestionnaireDecimalAnswer,
  readQuestionnaireSignatureInitials,
  readQuestionnaireStringAnswer,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/questionnaire-response-readers";
import { isLoincAffirmativeYesFromCoding } from "@/encounters/components/EncounterLabor/utils/loincAnswerCoding";
import { resolveFetalHeartRateRootGroup } from "@/encounters/components/EncounterLabor/utils/resolve-fetal-heart-rate-root-group";
import { YES_DISPLAY } from "@/intake-forms/constants/yes-no-display";

function readRelationToContractionsAnswers(item: QuestionnaireResponseItem | undefined): string[] {
  if (!item?.answer?.length) {
    return [];
  }
  return item.answer.reduce<string[]>((codings, answer) => {
    const coding = answer.valueCoding;
    if (coding?.code) {
      codings.push(JSON.stringify(coding));
    }
    return codings;
  }, []);
}

export function mapQuestionnaireResponseToFetalHeartRateFormValues(
  questionnaireResponse: QuestionnaireResponse | undefined,
): FetalHeartRateFlowsheetFormValues {
  // Hydration uses an empty shape so a missing/malformed root never prefills "now".
  const emptyValues = getEmptyFetalHeartRateFlowsheetFormValues();
  const root = resolveFetalHeartRateRootGroup(questionnaireResponse?.item);
  if (!root?.item) {
    return emptyValues;
  }

  const dateTimeGroup = findQuestionnaireResponseItem(
    root.item,
    FETAL_HEART_RATE_DATE_TIME_LINK_ID,
  );
  const dateAnswer = findQuestionnaireResponseItem(
    dateTimeGroup?.item,
    FETAL_HEART_RATE_DATE_LINK_ID,
  )?.answer?.[0]?.valueDate;
  const timeAnswer = findQuestionnaireResponseItem(
    dateTimeGroup?.item,
    FETAL_HEART_RATE_TIME_LINK_ID,
  )?.answer?.[0]?.valueTime;

  const beatsPerMinuteGroup = findQuestionnaireResponseItem(
    root.item,
    FETAL_HEART_RATE_BEATS_PER_MINUTE_LINK_ID,
  );

  const assessBaselineAnswer = findQuestionnaireResponseItem(
    root.item,
    FETAL_HEART_RATE_ASSESS_BASELINE_LINK_ID,
  )?.answer?.[0];
  const shouldHydrateBaseline =
    isYesAnswer(assessBaselineAnswer) ||
    isLoincAffirmativeYesFromCoding(assessBaselineAnswer?.valueCoding);
  const assessBaseline = shouldHydrateBaseline
    ? (readYesNoDisplayFromAnswer(assessBaselineAnswer) ?? YES_DISPLAY)
    : readYesNoDisplayFromAnswer(assessBaselineAnswer);

  return {
    ...emptyValues,
    fhrDate: parseQuestionnaireResponseLocalValueDate(dateAnswer),
    fhrTime: timeAnswer ?? null,
    fhrBPMLow: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(
        beatsPerMinuteGroup?.item,
        FETAL_HEART_RATE_BEATS_PER_MINUTE_LOW_LINK_ID,
      ),
    ),
    fhrBPMHigh: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(
        beatsPerMinuteGroup?.item,
        FETAL_HEART_RATE_BEATS_PER_MINUTE_HIGH_LINK_ID,
      ),
    ),
    fhrDurationOfListening: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(root.item, FETAL_HEART_RATE_DURATION_OF_LISTENING_LINK_ID),
    ),
    fhrListeningDevice: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, FETAL_HEART_RATE_LISTENING_DEVICE_LINK_ID),
    ),
    fhrRelationToContractions: readRelationToContractionsAnswers(
      findQuestionnaireResponseItem(root.item, FETAL_HEART_RATE_RELATION_TO_CONTRACTIONS_LINK_ID),
    ),
    accelerationsDetected: readYesNoDisplayFromAnswer(
      findQuestionnaireResponseItem(root.item, FETAL_HEART_RATE_ACCELERATIONS_DETECTED_LINK_ID)
        ?.answer?.[0],
    ),
    decelerationsDetected: readYesNoDisplayFromAnswer(
      findQuestionnaireResponseItem(root.item, FETAL_HEART_RATE_DECELERATIONS_DETECTED_LINK_ID)
        ?.answer?.[0],
    ),
    assessBaseline,
    fhrBaseline: shouldHydrateBaseline
      ? readQuestionnaireDecimalAnswer(
          findQuestionnaireResponseItem(root.item, FETAL_HEART_RATE_BASELINE_LINK_ID),
        )
      : emptyValues.fhrBaseline,
    fhrDurationOfListeningBaseline: shouldHydrateBaseline
      ? readQuestionnaireDecimalAnswer(
          findQuestionnaireResponseItem(
            root.item,
            FETAL_HEART_RATE_BASELINE_DURATION_OF_LISTENING_LINK_ID,
          ),
        )
      : emptyValues.fhrDurationOfListeningBaseline,
    fhrNotes: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, FETAL_HEART_RATE_NOTES_LINK_ID),
    ),
    fhrSignatureInitials: readQuestionnaireSignatureInitials(
      findQuestionnaireResponseItem(root.item, FETAL_HEART_RATE_SIGNATURE_INITIALS_LINK_ID),
    ),
  };
}

/** Shape expected by `getQuestionnaireResponseFromFormValues` / labor bots (legacy intake nesting). */
export function mapFetalHeartRateFormValuesToQuestionnaireValues(
  formValues: FetalHeartRateFlowsheetFormValues,
): Record<string, unknown> {
  const assessBaseline = formValues.assessBaseline === "Yes";
  const baselineHeartRate = assessBaseline ? formValues.fhrBaseline : "";
  const baselineDuration = assessBaseline ? formValues.fhrDurationOfListeningBaseline : "";

  return {
    [FETAL_HEART_RATE_ROOT_LINK_ID]: {
      [FETAL_HEART_RATE_DATE_TIME_LINK_ID]: {
        [FETAL_HEART_RATE_DATE_LINK_ID]: formValues.fhrDate,
        [FETAL_HEART_RATE_TIME_LINK_ID]: formValues.fhrTime,
      },
      [FETAL_HEART_RATE_BEATS_PER_MINUTE_LINK_ID]: {
        [FETAL_HEART_RATE_BEATS_PER_MINUTE_LOW_LINK_ID]: formValues.fhrBPMLow,
        [FETAL_HEART_RATE_BEATS_PER_MINUTE_HIGH_LINK_ID]: formValues.fhrBPMHigh,
      },
      [FETAL_HEART_RATE_DURATION_OF_LISTENING_LINK_ID]: formValues.fhrDurationOfListening,
      [FETAL_HEART_RATE_LISTENING_DEVICE_LINK_ID]: parseCodingFromNativeSelect(
        formValues.fhrListeningDevice,
      ),
      [FETAL_HEART_RATE_RELATION_TO_CONTRACTIONS_LINK_ID]: formValues.fhrRelationToContractions,
      [FETAL_HEART_RATE_ACCELERATIONS_DETECTED_LINK_ID]: yesNoDisplayToLoincCoding(
        formValues.accelerationsDetected,
      ),
      [FETAL_HEART_RATE_DECELERATIONS_DETECTED_LINK_ID]: yesNoDisplayToLoincCoding(
        formValues.decelerationsDetected,
      ),
      [FETAL_HEART_RATE_ASSESS_BASELINE_LINK_ID]: yesNoDisplayToLoincCoding(
        formValues.assessBaseline,
      ),
      [FETAL_HEART_RATE_BASELINE_LINK_ID]: baselineHeartRate,
      [FETAL_HEART_RATE_BASELINE_DURATION_OF_LISTENING_LINK_ID]: baselineDuration,
      [FETAL_HEART_RATE_NOTES_LINK_ID]: formValues.fhrNotes,
      [FETAL_HEART_RATE_SIGNATURE_INITIALS_LINK_ID]: formValues.fhrSignatureInitials,
    },
  };
}
